// Dynamic loading from CDN using fetch
class IntlPhoneInput extends HTMLElement {
  static parsePhoneNumberWithError;
  static isValidPhoneNumber;
  static metadata;
  static countries;
  static countryNames = new Intl.DisplayNames(["en"], { type: "region" });

  isValid;
  phoneNumber;
  parsedNumber;
  excludedCountries;
  onlyCountries;

  country;
  flagIcon;
  countryCode;
  flagButton;
  _popover;
  _searchInput;
  countryList;
  countryOptions;
  input;
  hiddenInput;

  libphonenumberUrl = "https://cdn.jsdelivr.net/npm/libphonenumber-js@1.12.24/bundle/libphonenumber-max.js";
  metadataUrl = "https://cdn.jsdelivr.net/npm/libphonenumber-js@1.12.24/metadata.full.json";
  flagIconsUrl = "https://cdn.jsdelivr.net/npm/flag-icons@7.5.0/css/flag-icons.min.css";

  constructor() {
    super();
    this.isValid = false;
    this.phoneNumber = null;
    this.parsedNumber = null;
    this.country = null;
    this.flagIcon = null;
    this.countryCode = null;
    this.flagButton = null;
    this._popover = null;
    this._searchInput = null;
    this.countryList = null;
    this.countryOptions = [];
    this.input = null;
    this.hiddenInput = null;
    this.excludedCountries = null;
    this.onlyCountries = null;
  }

  async connectedCallback() {
    this.libphonenumberUrl = this.getAttribute("libphonenumber-url") ?? this.libphonenumberUrl;
    this.metadataUrl = this.getAttribute("metadata-url") ?? this.metadataUrl;
    this.flagIconsUrl = this.getAttribute("flag-icons-url") ?? this.flagIconsUrl;

    this.country = this.getAttribute("default-country") ?? "AU";
    this.input = this.querySelector("input[data-phone]");
    this.hiddenInput = this.querySelector("input[data-phone-parsed]");
    this.isValid = false;
    this.excludedCountries = this.getAttribute("exclude-countries")
      ? this.getAttribute("exclude-countries")
          .split(",")
          .map((c) => c.trim().toUpperCase())
      : null;
    this.onlyCountries = this.getAttribute("only-countries")
      ? this.getAttribute("only-countries")
          .split(",")
          .map((c) => c.trim().toUpperCase())
      : null;

    // Load dependencies if not already loaded
    if (!IntlPhoneInput.countries) {
      await this.loadDependencies();
    }

    // Create flag button
    this.flagButton = document.createElement("button");
    this.flagButton.className = "flag-button";
    this.flagButton.type = "button";

    this.flagIcon = document.createElement("span");
    this.flagIcon.className = "fi";

    this.countryCode = document.createElement("span");
    this.countryCode.textContent = this.country;

    const arrow = document.createElement("span");
    arrow.className = "arrow";
    arrow.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M10.8125 3.47913L6 8.29163L1.1875 3.47913" stroke="#3D3939" style="stroke:#3D3939;stroke:color(display-p3 0.2392 0.2235 0.2235);stroke-opacity:1;"/></svg>';

    this.flagButton.appendChild(this.flagIcon);
    this.flagButton.appendChild(this.countryCode);
    this.flagButton.appendChild(arrow);

    // Create popover
    this._popover = document.createElement("div");
    this._popover.className = "country-popover";

    // Create search input
    this._searchInput = document.createElement("input");
    this._searchInput.type = "text";
    this._searchInput.className = "country-search";
    this._searchInput.placeholder = "Search countries...";
    this._searchInput.setAttribute("aria-label", "Search countries");

    // Create country list
    this.countryList = document.createElement("div");
    this.countryList.className = "country-list";
    this.countryList.setAttribute("role", "listbox");

    // Populate country options
    this.countryOptions = [];
    IntlPhoneInput.countries.forEach(this.populateCountry.bind(this));

    this._popover.appendChild(this._searchInput);
    this._popover.appendChild(this.countryList);

    // Insert elements
    if (this.input) {
      this.insertBefore(this.flagButton, this.input);
    } else {
      this.appendChild(this.flagButton);
    }
    this.appendChild(this._popover);

    // Set initial country
    this.changeCountry(this.country);

    // Event listeners
    this.flagButton.addEventListener("click", this.clickEvent.bind(this));
    this._searchInput.addEventListener("input", this.searchInputEvent.bind(this));
    this._searchInput.addEventListener("keydown", this.searchKeydownEvent.bind(this));
    this.countryList.addEventListener("keydown", this.countryListKeydownEvent.bind(this));
    this.input?.addEventListener("input", this.validatePhoneNumber.bind(this));
  }

  clickEvent = (e) => {
    e.stopPropagation();
    this.togglePopover();
    document.addEventListener("click", this.closePopoverEvent.bind(this), { once: true });
  };

  searchInputEvent = (e) => {
    this.filterCountries(e.target.value);
  };

  searchKeydownEvent = (e) => {
    if (e.key === "Escape") {
      this.closePopover();
      this.flagButton?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const firstVisible = this.countryList?.querySelector('.country-option:not([style*="display: none"])');
      if (firstVisible) firstVisible.focus();
    }
  };

  closePopoverEvent = (e) => {
    if (!this.contains(e.target)) {
      this.closePopover();
    }
  };

  countryListKeydownEvent = (e) => {
    if (e.key === "Escape") {
      this.closePopover();
      this.flagButton?.focus();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const options = Array.from(this.countryList.querySelectorAll('.country-option:not([style*="display: none"])'));
      const currentIndex = options.indexOf(document.activeElement);

      if (e.key === "ArrowDown" && currentIndex < options.length - 1) {
        options[currentIndex + 1].focus();
      } else if (e.key === "ArrowUp") {
        if (currentIndex > 0) {
          options[currentIndex - 1].focus();
        } else {
          this._searchInput?.focus();
        }
      }
    } else if (e.key === "Enter" && document.activeElement?.classList.contains("country-option")) {
      document.activeElement.click();
    }
  };

  populateCountry(country) {
    if ((this.onlyCountries && !this.onlyCountries.includes(country)) || this.excludedCountries?.includes(country)) {
      return;
    }
    const option = document.createElement("button");
    option.type = "button";
    option.className = "country-option";
    option.dataset.country = country;
    option.dataset.countryName = IntlPhoneInput.countryNames.of(country) || country;
    option.setAttribute("role", "option");

    const flag = document.createElement("span");
    flag.className = `fi fi-${country.toLowerCase()}`;

    const name = document.createElement("span");
    name.className = "country-name";
    name.textContent = IntlPhoneInput.countryNames.of(country) || country;

    const code = document.createElement("span");
    code.className = "country-code";
    code.textContent = country;

    option.appendChild(flag);
    option.appendChild(name);
    option.appendChild(code);

    option.addEventListener("click", () => {
      this.changeCountry(country);
      this.closePopover();
      this.input?.focus();
    });

    this.countryList.appendChild(option);
    this.countryOptions.push(option);
  }

  loadFlagIcons = () => {
    return new Promise((resolve) => {
      const existing = document.querySelector('link[href*="flag-icons"]');
      if (existing) {
        resolve();
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = this.flagIconsUrl;
      link.onload = () => resolve();
      link.onerror = () => resolve(); // Resolve anyway to not block
      document.head.appendChild(link);
    });
  };

  loadDependencies = async () => {
    const [, _libphonenumberResponse, metadataResponse] = await Promise.all([
      this.loadFlagIcons(),
      new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = this.libphonenumberUrl;
        script.async = true;

        script.onload = () => {
          if (window.libphonenumber) {
            IntlPhoneInput.parsePhoneNumberWithError = window.libphonenumber.parsePhoneNumberWithError;
            IntlPhoneInput.isValidPhoneNumber = window.libphonenumber.isValidPhoneNumber;
            resolve();
          } else {
            reject(new Error("libphonenumber not loaded"));
          }
        };
        script.onerror = () => reject(new Error("Failed to load libphonenumber"));
        document.head.appendChild(script);
      }),
      fetch(this.metadataUrl).then((r) => r.json()),
    ]);

    IntlPhoneInput.metadata = metadataResponse;
    IntlPhoneInput.countries = Object.keys(IntlPhoneInput.metadata.countries);
  };

  changeCountry(country) {
    this.country = country;
    this.flagIcon.className = `fi fi-${country.toLowerCase()}`;
    this.countryCode.textContent = country;

    // Update selected state in popover
    this.countryOptions.forEach((option) => {
      if (option.dataset.country === country) {
        option.classList.add("selected");
      } else {
        option.classList.remove("selected");
      }
    });

    // Revalidate phone number with new country
    if (this.input && this.phoneNumber) {
      this.validatePhoneNumber();
    } else {
      // Dispatch country change event even when there's no phone number
      this.dispatchEvent(new CustomEvent("changephone", { detail: this.customEventData(), bubbles: true }));
    }
  }

  validatePhoneNumber() {
    this.phoneNumber = this.input?.value || "";
    this.isValid = IntlPhoneInput.isValidPhoneNumber(this.phoneNumber, this.country || undefined);

    if (this.isValid) {
      try {
        const parsedNumber = IntlPhoneInput.parsePhoneNumberWithError(this.phoneNumber, this.country || "AU");
        this.parsedNumber = parsedNumber;

        // Update hidden input with parsed phone number in international format
        if (this.hiddenInput && this.parsedNumber) {
          this.hiddenInput.value = this.parsedNumber.formatInternational().replace(/ /g, "");
          this.dispatchEvent(new CustomEvent("changephone", { detail: this.customEventData(), bubbles: true }));
        }
      } catch (error) {
        console.log("Parse error:", error.message);
        // Clear hidden input if parsing fails
        if (this.hiddenInput) {
          this.hiddenInput.value = "";
          this.dispatchEvent(new CustomEvent("changephone", { detail: this.customEventData(), bubbles: true }));
        }
      }
    } else {
      // Clear hidden input if number is invalid
      if (this.hiddenInput) {
        this.hiddenInput.value = "";
        this.dispatchEvent(new CustomEvent("changephone", { detail: this.customEventData(), bubbles: true }));
      }
    }

    this.input?.setAttribute("aria-invalid", `${!this.isValid}`);
  }

  customEventData() {
    return {
      parsed: this.hiddenInput?.value || null,
      input: this.phoneNumber,
      country: this.country,
      valid: this.isValid,
    };
  }

  togglePopover() {
    const isOpen = this._popover.classList.toggle("open");
    this.flagButton.classList.toggle("open");

    if (isOpen) {
      this._searchInput.value = "";
      this.filterCountries("");
      this._searchInput.focus();
      // Scroll selected country into view
      const selected = this._popover.querySelector(".country-option.selected");
      if (selected) {
        selected.scrollIntoView({ block: "nearest" });
      }
    }

    return isOpen;
  }

  closePopover() {
    this._popover.classList.remove("open");
    this.flagButton.classList.remove("open");
  }

  filterCountries(searchTerm) {
    const term = searchTerm.toLowerCase();
    let hasResults = false;

    this.countryOptions.forEach((option) => {
      const countryName = (option.dataset.countryName || "").toLowerCase();
      const countryCode = (option.dataset.country || "").toLowerCase();
      const matches = countryName.includes(term) || countryCode.includes(term);

      option.style.display = matches ? "flex" : "none";
      if (matches) hasResults = true;
    });

    // Show/hide no results message
    const noResults = this._popover.querySelector(".no-results");
    if (noResults) {
      noResults.style.display = hasResults ? "none" : "block";
    } else if (!hasResults) {
      const msg = document.createElement("div");
      msg.className = "no-results";
      msg.textContent = "No countries found";
      this.countryList.appendChild(msg);
    }
  }
}

if (!customElements.get("intl-phone-input")) {
  customElements.define("intl-phone-input", IntlPhoneInput);
}